import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../config';
import SchemaList from './SchemaList';
import CreateSchemaModal from './CreateSchemaModal';
import EditSchemaModal from './EditSchemaModal';
import {
  Layers,
  Plus,
  Sparkles,
  Search,
  ShieldCheck,
  FileJson,
  KeyRound,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import './SchemaRegistry.css';

const SchemaRegistry = () => {
  const { token, tenant } = useAuth();
  const [schemas, setSchemas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Client-side (instant)
  const [localSearch, setLocalSearch] = useState('');

  // Text user is typing
  const [serverSearchInput, setServerSearchInput] = useState('');

  // Search actually applied to API
  const [serverSearchApplied, setServerSearchApplied] = useState('');

  // Pagination and filter states
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Modal and selection state
  const [selectedSchema, setSelectedSchema] = useState(null);
  const [expandedSchemaId, setExpandedSchemaId] = useState(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createInitialTab, setCreateInitialTab] = useState('visual');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAuthOverride, setShowAuthOverride] = useState(false);

  // Auth states from localStorage or props
  const [apiKey, setApiKey] = useState('');
  const [tenantUid, setTenantUid] = useState(tenant?.uid || '');

  const getHeaders = () => {
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (apiKey) headers['X-API-KEY'] = apiKey;
    if (tenantUid) headers['X-TENANT-ID'] = tenantUid;
    return headers;
  };

  useEffect(() => {
    if (tenant?.uid) {
      setTenantUid(tenant.uid);
    } else {
      const savedTenantUid = localStorage.getItem('vaultize_tenant_uid');
      if (savedTenantUid) setTenantUid(savedTenantUid);
    }

    const savedApiKey = localStorage.getItem('vaultize_api_key');
    if (savedApiKey) {
      setApiKey(savedApiKey);
    } else if (token) {
      fetch(`${API_BASE}/dashboard/api-keys`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then((res) => (res.ok ? res.json() : []))
        .then((keys) => {
          if (Array.isArray(keys) && keys.length > 0) {
            const activeKey = keys.find((k) => k.enabled && !k.revoked) || keys[0];
            const val = activeKey.apiKeyPreview || activeKey.apiKey || activeKey.maskedKey;
            if (val) setApiKey(val);
          }
        })
        .catch(() => {});
    }
  }, [tenant, token]);

  useEffect(() => {
    if ((apiKey && tenantUid) || token) {
      loadSchemas(page, pageSize, serverSearchApplied);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey, tenantUid, token, page, pageSize, serverSearchApplied]);

  const loadSchemas = async (currPage, size, filter) => {
    setLoading(true);
    setError('');

    try {
      const backendPage = currPage - 1;
      const countParams = new URLSearchParams({
        page: backendPage.toString(),
        pageSize: size.toString(),
        search: filter || '',
        sort: 'createdAt,desc'
      });

      const res = await fetch(
        `${API_BASE}/admin/schemas/count?${countParams}`,
        { headers: getHeaders() }
      );

      if (!res.ok) throw new Error('Failed to load schemas');

      const pageData = await res.json();
      setTotalCount(pageData.totalElements || 0);
      setSchemas(pageData.content || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = (initialTab = 'visual') => {
    setCreateInitialTab(initialTab);
    setShowCreateModal(true);
  };
  const closeCreateModal = () => setShowCreateModal(false);

  const openEditModal = (schema) => {
    setSelectedSchema(schema);
    setShowEditModal(true);
  };
  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedSchema(null);
  };

  const locallyFilteredSchemas = schemas.filter((s) => {
    if (!localSearch.trim()) return true;
    const term = localSearch.toLowerCase();
    return (
      s.name.toLowerCase().includes(term) ||
      (s.description || '').toLowerCase().includes(term)
    );
  });

  const toggleVersions = (schema) => {
    setExpandedSchemaId((prev) => (prev === schema.id ? null : schema.id));
  };

  return (
    <section className="schema-page">
      <div className="container">
        {/* Enterprise Page Header */}
        <div className="schema-hero-bar">
          <div className="schema-hero-left">
            <span className="schema-kicker-badge">
              <ShieldCheck size={14} /> Data Governance &amp; Tokenization Contracts
            </span>
            <h1 className="schema-title">Schema Registry</h1>
            <p className="schema-subtitle">
              Enforce strict field-level PII classification, Luhn-preserving FPE, and masking policies across your JSON APIs and GenAI pipelines.
            </p>
          </div>

          <div className="schema-hero-actions">
            <button
              type="button"
              className="btn-schema-ai-cta"
              onClick={() => openCreateModal('ai_convert')}
            >
              <Sparkles size={16} />
              <span>Convert Sample JSON to Schema</span>
            </button>
            <button
              type="button"
              className="btn btn-primary schema-create-btn"
              onClick={() => openCreateModal('visual')}
            >
              <Plus size={16} />
              <span>Create New Schema</span>
            </button>
          </div>
        </div>

        {/* Summary KPI Strip */}
        <div className="schema-kpi-strip">
          <div className="schema-kpi-card">
            <div className="schema-kpi-icon emerald">
              <Layers size={18} />
            </div>
            <div>
              <span className="schema-kpi-label">Registered Schemas</span>
              <strong className="schema-kpi-value">{totalCount} Active Contracts</strong>
            </div>
          </div>

          <div className="schema-kpi-card">
            <div className="schema-kpi-icon blue">
              <FileJson size={18} />
            </div>
            <div>
              <span className="schema-kpi-label">Schema Validation</span>
              <strong className="schema-kpi-value">Strict Structural Enforcement</strong>
            </div>
          </div>

          <div className="schema-kpi-card">
            <div className="schema-kpi-icon purple">
              <Sparkles size={18} />
            </div>
            <div>
              <span className="schema-kpi-label">Payload-to-Schema Engine</span>
              <strong className="schema-kpi-value">Semantic AI + Gemini / OpenAI</strong>
            </div>
          </div>
        </div>

        {error && <div className="schema-error">{error}</div>}

        {/* Search & Filter Toolbar */}
        <div className="schema-toolbar-card">
          <div className="schema-search-row">
            <div className="schema-input-with-icon">
              <Search size={15} className="schema-search-icon" />
              <input
                type="search"
                placeholder="Quick filter current page..."
                value={localSearch}
                onChange={(e) => setLocalSearch(e.target.value)}
                className="search-input with-icon"
              />
            </div>

            <div className="schema-server-search-group">
              <div className="schema-input-with-icon">
                <Search size={15} className="schema-search-icon" />
                <input
                  type="search"
                  placeholder="Search all registered schemas by name..."
                  value={serverSearchInput}
                  onChange={(e) => setServerSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setPage(1);
                      setServerSearchApplied(serverSearchInput.trim());
                    }
                  }}
                  className="search-input with-icon"
                />
              </div>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setPage(1);
                  setServerSearchApplied(serverSearchInput.trim());
                }}
              >
                Search Registry
              </button>
            </div>

            <button
              type="button"
              className="btn-auth-context-toggle"
              onClick={() => setShowAuthOverride(!showAuthOverride)}
              title="Inspect or override API Key / Tenant UID headers"
            >
              <KeyRound size={14} />
              <span>Header Context</span>
              {showAuthOverride ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          </div>

          {showAuthOverride && (
            <div className="schema-auth-override-drawer">
              <div className="auth-override-field">
                <label>X-API-KEY Override (Optional when signed in)</label>
                <input
                  className="form-input"
                  placeholder="API Key"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                />
              </div>
              <div className="auth-override-field">
                <label>X-TENANT-ID Override</label>
                <input
                  className="form-input"
                  placeholder="Tenant UID"
                  value={tenantUid}
                  onChange={(e) => setTenantUid(e.target.value)}
                />
              </div>
            </div>
          )}
        </div>

        <SchemaList
          schemas={locallyFilteredSchemas}
          expandedSchemaId={expandedSchemaId}
          onToggleVersions={toggleVersions}
          onEditSchema={openEditModal}
          onCreateSchema={() => openCreateModal('visual')}
          onConvertSample={() => openCreateModal('ai_convert')}
          loading={loading}
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={setPage}
        />

        {showCreateModal && (
          <CreateSchemaModal
            apiKey={apiKey}
            tenantUid={tenantUid}
            token={token}
            initialMode={createInitialTab}
            onClose={closeCreateModal}
            onCreated={() => {
              closeCreateModal();
              loadSchemas(page, pageSize, serverSearchApplied);
            }}
          />
        )}

        {showEditModal && selectedSchema && (
          <EditSchemaModal
            schema={selectedSchema}
            apiKey={apiKey}
            tenantUid={tenantUid}
            token={token}
            onClose={closeEditModal}
            onUpdated={() => {
              closeEditModal();
              loadSchemas(page, pageSize, serverSearchApplied);
            }}
          />
        )}
      </div>
    </section>
  );
};

export default SchemaRegistry;
